import React, { useState, useEffect, useCallback } from 'react';
import { formatCurrency, formatDate, downloadCSV } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { Pagination } from '../../components/Pagination';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Users, Loader2, Search, Download, Eye, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { api, tenantApi, formatApiErrorDetail } from '../../lib/api';

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 0 });
  const [search, setSearch] = useState('');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (tenantFilter !== 'all') params.tenant_id = tenantFilter;
      const [custRes, tenantRes] = await Promise.all([
        api.get('/customers', { params }),
        tenantApi.list({ limit: 100 }),
      ]);
      setCustomers(custRes.data.items || []);
      setPagination({ total: custRes.data.total || 0, pages: custRes.data.pages || 0 });
      setTenants((tenantRes.data.items || tenantRes.data || []).filter(t => t.status === 'ACTIVE'));
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [page, search, tenantFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearch = (v) => { setSearch(v); setPage(1); };

  const handleExportCSV = () => {
    const data = customers.map(c => ({
      Customer: c.customer_name, Phone: c.customer_phone, Email: c.customer_email || '-',
      Device: `${c.device_brand} ${c.device_model}`, IMEI: c.device_imei,
      'Device Price': c.device_price, 'Sale Date': c.sale_date, Tenant: c.tenant_name,
      Branch: c.branch_name, Plan: c.warranty_plan, 'Base Price': c.warranty_base_price,
      'Sold Price': c.warranty_sold_price, Certificate: c.warranty_certificate || '-',
      'Valid From': c.warranty_start || '-', 'Valid To': c.warranty_end || '-', Status: c.warranty_status,
    }));
    downloadCSV(data, `customers-warranty-${formatDate(new Date())}`);
    toast.success('Exported to CSV');
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }} data-testid="customers-heading">
              Customers
            </h1>
            <p className="text-slate-600 mt-1">All warranty customers across tenants</p>
          </div>
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-customers-csv">
            <Download className="h-4 w-4 mr-2" />Export CSV
          </Button>
        </div>

        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="Search name, phone, IMEI..." value={search}
                  onChange={(e) => handleSearch(e.target.value)} className="pl-10 h-11" data-testid="search-customers" />
              </div>
              <Select value={tenantFilter} onValueChange={(v) => { setTenantFilter(v); setPage(1); }}>
                <SelectTrigger className="w-full sm:w-48" data-testid="tenant-filter"><SelectValue placeholder="All Tenants" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tenants</SelectItem>
                  {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" /></div>
            ) : customers.length > 0 ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-100">
                      <TableHead>Customer</TableHead>
                      <TableHead>Device</TableHead>
                      <TableHead>Tenant</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead className="text-right">Sold Price</TableHead>
                      <TableHead>Certificate</TableHead>
                      <TableHead>Validity</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customers.map(c => (
                      <TableRow key={c.sale_id} className="table-row-hover">
                        <TableCell>
                          <p className="font-medium text-slate-900">{c.customer_name}</p>
                          <p className="text-xs text-slate-500">{c.customer_phone}</p>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{c.device_brand} {c.device_model}</p>
                          <p className="text-xs text-slate-500 font-mono">{c.device_imei}</p>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{c.tenant_name}</TableCell>
                        <TableCell className="text-sm">{c.warranty_plan}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(c.warranty_sold_price)}</TableCell>
                        <TableCell className="text-xs font-mono text-[#005DE8]">{c.warranty_certificate || '-'}</TableCell>
                        <TableCell className="text-xs text-slate-600">
                          {c.warranty_start ? `${formatDate(c.warranty_start)} - ${formatDate(c.warranty_end)}` : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className={c.warranty_status === 'Active'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 border'
                            : 'bg-amber-50 text-amber-700 border-amber-200 border'}>
                            {c.warranty_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => setSelectedCustomer(c)} data-testid={`view-customer-${c.sale_id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Pagination page={page} pages={pagination.pages} total={pagination.total} onPageChange={setPage} />
              </>
            ) : (
              <div className="text-center py-16">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No customers found</h3>
                <p className="text-slate-500 mt-1">Warranty customers will appear here</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={!!selectedCustomer} onOpenChange={() => setSelectedCustomer(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Customer Warranty Details</DialogTitle>
            </DialogHeader>
            {selectedCustomer && (
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">Name:</span> <span className="font-medium">{selectedCustomer.customer_name}</span></div>
                  <div><span className="text-slate-500">Phone:</span> <span className="font-medium">{selectedCustomer.customer_phone}</span></div>
                  {selectedCustomer.customer_email && <div className="col-span-2"><span className="text-slate-500">Email:</span> <span className="font-medium">{selectedCustomer.customer_email}</span></div>}
                </div>
                <div className="border-t pt-3 grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">Device:</span> <span className="font-medium">{selectedCustomer.device_brand} {selectedCustomer.device_model}</span></div>
                  <div><span className="text-slate-500">IMEI:</span> <span className="font-mono text-xs">{selectedCustomer.device_imei}</span></div>
                  <div><span className="text-slate-500">Device Price:</span> <span className="font-medium">{formatCurrency(selectedCustomer.device_price)}</span></div>
                  <div><span className="text-slate-500">Sale Date:</span> <span className="font-medium">{formatDate(selectedCustomer.sale_date)}</span></div>
                </div>
                <div className="border-t pt-3 grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">Tenant:</span> <span className="font-medium">{selectedCustomer.tenant_name}</span></div>
                  <div><span className="text-slate-500">Branch:</span> <span className="font-medium">{selectedCustomer.branch_name}</span></div>
                </div>
                <div className="border-t pt-3 bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck className="h-5 w-5 text-[#005DE8]" />
                    <span className="font-semibold text-slate-900">Warranty Info</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-slate-500">Plan:</span> <span className="font-medium">{selectedCustomer.warranty_plan}</span></div>
                    <div><span className="text-slate-500">Duration:</span> <span className="font-medium">{selectedCustomer.warranty_duration} months</span></div>
                    <div><span className="text-slate-500">Base Price:</span> <span className="font-medium">{formatCurrency(selectedCustomer.warranty_base_price)}</span></div>
                    <div><span className="text-slate-500">Sold Price:</span> <span className="font-medium text-[#005DE8]">{formatCurrency(selectedCustomer.warranty_sold_price)}</span></div>
                    <div><span className="text-slate-500">Certificate:</span> <span className="font-mono font-medium">{selectedCustomer.warranty_certificate || 'N/A'}</span></div>
                    <div><span className="text-slate-500">Status:</span>
                      <Badge className={`ml-1 ${selectedCustomer.warranty_status === 'Active' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                        {selectedCustomer.warranty_status}
                      </Badge>
                    </div>
                    {selectedCustomer.warranty_start && (
                      <div className="col-span-2"><span className="text-slate-500">Validity:</span> <span className="font-medium">{formatDate(selectedCustomer.warranty_start)} to {formatDate(selectedCustomer.warranty_end)}</span></div>
                    )}
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedCustomer(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
